import { defineComponent as _defineComponent } from 'vue'
import { message } from "ant-design-vue"
import { storeToRefs } from "pinia"
import { computed, ref } from "vue"
import { getCurrentInstance, onBeforeUpdate, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router/composables"

import DescriptionItem from "@/components/Common/DescriptionItem.vue"
import SearchProducts from "@/components/Common/SearchProducts"
import { SearchedProduct } from "@/components/Common/SearchProducts/SearchProducts.vue"
import Thumbnail from "@/components/Common/Thumbnail"
import { FetcherParams, useAntTable } from "@/composables/useAntTable"
import i18n from "@/i18n"
import ApproveWindow from "@/modules/sync/_components/ApproveWindow"
import DeleteSyncModal from "@/modules/sync/_components/DeleteSyncModal.vue"
import StopSyncModal from "@/modules/sync/_components/StopSyncModal.vue"
import SyncDashboardCard from "@/modules/sync/_components/SyncDashboardCard.vue"
import SyncDraftsExpandedRow from "@/modules/sync/_components/SyncDraftsExpandedRow.vue"
import SyncParseErrorsModal from "@/modules/sync/_components/SyncParseErrorsModal.vue"
import { useSyncDraftsStore } from "@/modules/sync/store/useSyncDraftsStore"
import { useSyncMetaStore } from "@/modules/sync/store/useSyncMetaStore"
import { useSyncStore } from "@/modules/sync/store/useSyncStore"
import { getSyncTaskStatus, isTerminalSyncTaskStatus } from "@/modules/sync/syncTaskStatus"
import syncService from "@/services/syncService"
import { HANDLE_SUPPORT_DRAWER } from "@/store/modules/notifications/mutationTypes"
import { SyncDraft } from "@/types/sync.type"
import getFormattedPrice from "@/utils/getFormattedPrice"
import getProductState from "@/utils/getProductState"

import { useSyncSettings } from "../useSyncSettings"

import { buildDraftsQuery } from "./syncDraftFilters"
import { useSyncDraftsColumns } from "./useSyncDraftsColumns"

// Fix: Define dummy variables to hint types for Volar in legacy slot usage
// This is required because Volar cannot infer slot-scope types in usage of AntDV 1.x with script setup

export default /*#__PURE__*/_defineComponent({
  __name: 'SyncDrafts',
  setup(__props) {

const record = {} as SyncDraft
const images = [] as string[]
const quantity = 0 as number
const price = 0 as number
const draft = {} as SyncDraft
// End Fix

// Store & Route
const syncStore = useSyncStore()
const syncDraftsStore = useSyncDraftsStore()
const syncMetaStore = useSyncMetaStore()
const route = useRoute()
const router = useRouter()

const { synchronization, synchronizationsDraftsMeta, synchronizationsDraftsMetaFetching } =
  storeToRefs(syncStore)
const { drafts, count, fetching } = storeToRefs(syncDraftsStore)
const { proxy } = getCurrentInstance()!
// End Store & Route

// State
const selectedTab = ref("all")
const searchProductsIsOpened = ref(false)
const approveWindowisOpened = ref(false)
const searchDraftValue = ref("")
const selectedRowKeys = ref([])
const currentDraftName = ref("")
const currentDraftUuid = ref(null)
const approveWindowPayload = ref<SyncDraft | null>(null)
const draftRefs = ref<Record<string, any>>({})

const syncUuid = route.params.uuid

const bulkUpdateLoading = ref(false)
const parseErrorsOpened = ref(false)

const parseErrors = computed(() => synchronization.value?.errors?.parse_errors || [])
// End State

// Composables
const {
  isDeleteModalOpened,
  isStopModalOpened,
  currentSyncUuid,
  handleOpenStopModal,
  handleOpenDeleteModal,
  handleCloseStopModal,
  handleCloseDeleteModal,
  handleStartSync
} = useSyncSettings()
const { columns } = useSyncDraftsColumns()

const fetchDrafts = (params: FetcherParams) => {
  const { page, pageSize, ordering, filters } = params

  return syncDraftsStore.getDrafts({
    page,
    pageSize,
    uuid: syncUuid,
    query: buildDraftsQuery({
      filters,
      ordering,
      selectedTab: selectedTab.value,
      searchDraftValue: searchDraftValue.value
    })
  })
}

const { hasTableFilters, resetTableFilters, tablePagination, onTableChange, refreshTable } =
  useAntTable({
    key: "drafts",
    position: "both",
    total: count,
    fetcher: fetchDrafts
  })
// End Composables

// Computed
const selectedKeysNeedApprove = computed(() => {
  const selectedDraftsNeedApprove = drafts.value
    .filter((draft) => selectedRowKeys.value.includes(draft.uuid))
    .filter((draft) => {
      const isNeedAction = draft.is_need_action
      const withProduct = draft.product !== null
      const isInvalid = draft.status.key === "INVALID"

      return isNeedAction && withProduct && !isInvalid
    })

  return selectedDraftsNeedApprove.map((draft) => draft.uuid)
})
const hasFilters = computed(() => {
  return hasTableFilters.value || searchDraftValue.value.length > 0 || selectedTab.value !== "all"
})
const statusFailure = computed(() => {
  return synchronization.value && synchronization.value.status === "FAILURE"
})
const tabs = computed(() => {
  return [
    {
      label: i18n.t("synchronization.tab_all"),
      key: "all"
    },
    {
      label: i18n.t("synchronization.need_action"),
      key: "is_need_action",
      count: synchronizationsDraftsMeta.value.offersIsNeedActionCount
    }
  ]
})
// End Computed

// Methods
const handleBulkUpdate = () => {
  const payload = selectedKeysNeedApprove.value.map((uuid) => ({
    uuid,
    _approve: true
  }))

  bulkUpdateLoading.value = true

  syncDraftsStore
    .updateDrafts({
      payload,
      synchronizationUuid: syncUuid
    })
    .then((response) => {
      pollSyncTask(response.meta.task_id)
    })
    .catch((error) => {
      message.error(i18n.t("synchronization.drafts_not_updated") as string)
      bulkUpdateLoading.value = false
    })
    .finally(() => {
      selectedRowKeys.value = []
    })
}
const pollSyncTask = async (taskId: string): Promise<string | null> => {
  if (!taskId) return null

  const poll = async (): Promise<string | null> => {
    try {
      const response = await syncService.trackTask({
        synchronizationUuid: syncUuid,
        taskUuid: taskId
      })

      if (isTerminalSyncTaskStatus(getSyncTaskStatus(response.data))) {
        refreshTable()
        syncStore.getSyncDraftsMeta(syncUuid)
        bulkUpdateLoading.value = false
      } else {
        // Чекаємо pollInterval і потім рекурсивно викликаємо poll
        await new Promise((resolve) => setTimeout(resolve, 3000))
        return poll()
      }
    } catch (error) {
      refreshTable()
      syncStore.getSyncDraftsMeta(syncUuid)
      message.error(i18n.t("synchronization.drafts_not_updated") as string)
      bulkUpdateLoading.value = false
    }
  }

  return poll()
}
const handleResetAllFilters = () => {
  searchDraftValue.value = ""
  selectedTab.value = "all"
  resetTableFilters()
}
const updateSyncCallback = () => {
  syncStore.getSynchronization(syncUuid)
}
const handleChangeTab = (key: string) => {
  selectedTab.value = key
  refreshTable()
}
const chatWithModerator = () => {
  router.push({ name: "messenger", query: { openSupport: "true" } })
}
const ticketWithSupport = () => {
  proxy.$store.commit(`notifications/${HANDLE_SUPPORT_DRAWER}`, {
    open: true
  })
}
const setDraftRef = (el: any, uuid: string) => {
  if (el) {
    draftRefs.value[`draft_${uuid}`] = el
  }
}
const hasProperties = (object: Record<string, unknown>) => Object.keys(object).length > 0
const getConditionValueByDraft = (draft: SyncDraft): any => {
  const { condition_key: backKey, condition_value: backValue } = draft
  const state = getProductState(backKey, backValue)

  return `${state.backKey}_${state.value}`
}
const handleChangeProduct = ({ draftUuid, draft }: { draftUuid: string; draft: SyncDraft }) => {
  currentDraftUuid.value = draftUuid
  currentDraftName.value = draft.name
  searchProductsIsOpened.value = true
}
const handleShowApproveWindow = ({ draft }: { draft: SyncDraft }) => {
  approveWindowPayload.value = draft
  approveWindowisOpened.value = true
}
const handleSearchDrafts = () => {
  refreshTable()
}
const handleSelectRows = (keys: string[]) => {
  selectedRowKeys.value = keys
}
const handleProductSelect = (product: SearchedProduct) => {
  const draftRef = draftRefs.value[`draft_${currentDraftUuid.value}`]

  draftRef.productSelect(product)

  closeSearchProductsDrawer()
}
const handleCloseApproveWindow = () => {
  approveWindowisOpened.value = false
  approveWindowPayload.value = null
}
const handleDraftApproved = async () => {
  handleCloseApproveWindow()
  try {
    await Promise.all([refreshTable(), syncStore.getSyncDraftsMeta(syncUuid)])
  } catch {
    message.error(i18n.t("synchronization.drafts_not_updated") as string)
  }
}
const closeSearchProductsDrawer = () => {
  searchProductsIsOpened.value = false
}
const onOfferDeleted = () => {
  refreshTable()
  syncStore.getSyncDraftsMeta(syncUuid)
}
// End Methods

// Lifecycle
onMounted(() => {
  refreshTable()
  syncStore.getSyncDraftsMeta(syncUuid)
  syncStore.getSynchronization(syncUuid)
  syncMetaStore.getAllActiveDeliveries()
})
onBeforeUpdate(() => {
  draftRefs.value = {}
})
// End Lifecycle

return { __sfc: true,record, images, quantity, price, draft, syncStore, syncDraftsStore, syncMetaStore, route, router, synchronization, synchronizationsDraftsMeta, synchronizationsDraftsMetaFetching, drafts, count, fetching, proxy, selectedTab, searchProductsIsOpened, approveWindowisOpened, searchDraftValue, selectedRowKeys, currentDraftName, currentDraftUuid, approveWindowPayload, draftRefs, syncUuid, bulkUpdateLoading, parseErrorsOpened, parseErrors, isDeleteModalOpened, isStopModalOpened, currentSyncUuid, handleOpenStopModal, handleOpenDeleteModal, handleCloseStopModal, handleCloseDeleteModal, handleStartSync, columns, fetchDrafts, hasTableFilters, resetTableFilters, tablePagination, onTableChange, refreshTable, selectedKeysNeedApprove, hasFilters, statusFailure, tabs, handleBulkUpdate, pollSyncTask, handleResetAllFilters, updateSyncCallback, handleChangeTab, chatWithModerator, ticketWithSupport, setDraftRef, hasProperties, getConditionValueByDraft, handleChangeProduct, handleShowApproveWindow, handleSearchDrafts, handleSelectRows, handleProductSelect, handleCloseApproveWindow, handleDraftApproved, closeSearchProductsDrawer, onOfferDeleted, DescriptionItem, SearchProducts, Thumbnail, i18n, ApproveWindow, DeleteSyncModal, StopSyncModal, SyncDashboardCard, SyncDraftsExpandedRow, SyncParseErrorsModal, getFormattedPrice }
}

})